import { Component, ChangeDetectionStrategy, signal, inject, computed, effect, viewChild, ElementRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { PERMISSION_UI_DIRECTIVES } from '../../directives/permission.directive';
import { debounceTime, distinctUntilChanged, finalize, merge } from 'rxjs';
import { SubscriptionService } from '../../services/subscription.service';
import { AuthService } from '../../services/auth.service';
import { httpErrorApiMessage } from '../../core/api-response-utils';
import { SubscriptionTimelineService, type TimelineGroupMode } from '../../services/subscription-timeline.service';
import { SaleService } from '../../services/sale.service';
import { CustomerService, CustomerDTO } from '../../services/customer.service';
import type {
  CollectionMethodEnumValue,
  ChargeDTO,
  SaleDTO,
  SubscriptionDTO,
  SubscriptionStatusEnumValue,
  FrequencyEnumValue,
  ListSubscriptionFilterSpec
} from '../../core/models';
import { CollectionMethodEnum, EntityState } from '../../core/models';
import { formatApiDateOnly, calendarDaysUntil, parseApiCalendarDate } from '../../core/date-utils';
import { SubscriptionReadComponent } from './subscription-read.component';
import { SaleReadComponent } from '../sales/sale-read.component';
import { CustomerReadComponent } from '../customers/customer-read.component';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { SkeletonModule } from 'primeng/skeleton';
import { FloatLabelModule } from 'primeng/floatlabel';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectModule } from 'primeng/select';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { DrawerModule } from 'primeng/drawer';
import { ToastrService } from 'ngx-toastr';

type RenewalUrgency = 'urgent' | 'soon' | 'later';

export interface TimelineRenewalGroup {
  dateKey: string;
  title: string;
  subtitle: string;
  dayNumber: string;
  monthLabel: string;
  monthKey: string;
  monthTitle: string;
  count: number;
  totalAmount: number;
  totalCurrency: string | null;
  items: SubscriptionDTO[];
  urgency: RenewalUrgency;
}

@Component({
  selector: 'app-subscriptions-list',
  templateUrl: './subscriptions-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  styles: [
    `
      :host {
        display: flex;
        flex-direction: column;
        flex: 1 1 0%;
        min-height: 0;
        width: 100%;
      }
      :host ::ng-deep .subs-primeng-table .p-datatable-thead > tr > th {
        background: rgb(248 250 252);
        border-color: rgb(226 232 240);
        color: rgb(100 116 139);
        font-size: 0.75rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        padding: 0.55rem 0.5rem !important;
        transition: background-color 0.15s ease, color 0.15s ease;
      }
      :host ::ng-deep .subs-primeng-table .p-datatable-thead > tr > th.p-sortable-column {
        cursor: pointer;
        user-select: none;
      }
      :host ::ng-deep .subs-primeng-table .p-datatable-thead > tr > th.p-sortable-column:hover {
        background: rgb(241 245 249);
        color: rgb(15 23 42);
      }
      :host ::ng-deep .subs-primeng-table .p-datatable-thead > tr > th.p-sortable-column:focus-visible {
        outline: 2px solid rgb(14 165 233);
        outline-offset: -2px;
      }
      :host ::ng-deep .subs-primeng-table .p-datatable-thead > tr > th.p-sortable-column.p-datatable-column-sorted {
        background: rgb(240 249 255);
        color: rgb(3 105 161);
      }
      :host ::ng-deep .subs-primeng-table .p-datatable-thead > tr > th.p-sortable-column .p-datatable-sort-icon,
      :host ::ng-deep .subs-primeng-table .p-datatable-thead > tr > th.p-sortable-column .p-sortable-column-icon,
      :host ::ng-deep .subs-primeng-table .p-datatable-thead > tr > th.p-sortable-column p-sorticon svg {
        width: 0.75rem;
        height: 0.75rem;
        color: rgb(14 165 233);
        opacity: 1;
        flex-shrink: 0;
        margin-left: auto;
      }
      :host ::ng-deep .subs-primeng-table .p-datatable-tbody > tr > td {
        padding: 0.4rem 0.45rem !important;
      }
      :host ::ng-deep .subs-primeng-table .p-datatable-table {
        width: 100%;
        table-layout: auto;
      }
      :host ::ng-deep p-table {
        display: flex;
        flex-direction: column;
        height: 100%;
        min-height: 0;
      }
      :host ::ng-deep .subs-primeng-table.p-datatable,
      :host ::ng-deep .p-datatable {
        display: flex;
        flex-direction: column;
        height: 100%;
        min-height: 0;
      }
      :host ::ng-deep .p-datatable-table-container,
      :host ::ng-deep .p-datatable-flex-scrollable > .p-datatable-table-container {
        flex: 1 1 0;
        min-height: 0;
        overflow: auto;
      }
      :host ::ng-deep .p-datatable-paginator-bottom {
        flex-shrink: 0;
      }
      :host ::ng-deep .p-paginator .p-paginator-page.p-paginator-page-selected,
      :host ::ng-deep button.p-paginator-page-selected {
        background: rgb(224 242 254) !important;
        border-color: rgb(14 165 233) !important;
        color: rgb(2 132 199) !important;
      }
      :host ::ng-deep .subscription-details-drawer .p-drawer-header {
        background: rgb(241 245 249);
        border-bottom: 1px solid rgb(226 232 240);
        box-shadow: 0 1px 3px 0 rgb(15 23 42 / 0.06);
        padding: 1rem 1.25rem;
      }
      :host ::ng-deep .subscription-details-drawer .p-drawer-content {
        background: rgb(248 250 252);
        padding: 1rem 1.25rem;
      }
      :host ::ng-deep .subscription-sale-drawer .p-drawer-header {
        background: rgb(241 245 249);
        border-bottom: 1px solid rgb(226 232 240);
        box-shadow: 0 1px 3px 0 rgb(15 23 42 / 0.06);
        padding: 1rem 1.25rem;
      }
      :host ::ng-deep .subscription-sale-drawer .p-drawer-content {
        background: rgb(248 250 252);
        padding: 1rem 1.25rem;
      }
      :host ::ng-deep .subscription-customer-drawer .p-drawer-header {
        background: rgb(241 245 249);
        border-bottom: 1px solid rgb(226 232 240);
        box-shadow: 0 1px 3px 0 rgb(15 23 42 / 0.06);
        padding: 1rem 1.25rem;
      }
      :host ::ng-deep .subscription-customer-drawer .p-drawer-content {
        background: rgb(248 250 252);
        padding: 1rem 1.25rem;
      }
      .subs-timeline-rail {
        position: relative;
        padding-left: 4.75rem;
      }
      .subs-timeline-rail::before {
        content: '';
        position: absolute;
        left: 1.75rem;
        top: 0.5rem;
        bottom: 0;
        width: 2px;
        background: linear-gradient(
          180deg,
          rgb(45 212 191) 0%,
          rgb(203 213 225) 28%,
          rgb(226 232 240) 100%
        );
        border-radius: 9999px;
      }
      .subs-timeline-month {
        position: relative;
        z-index: 1;
        margin: 1.25rem 0 0.75rem;
        padding-left: 0.25rem;
        font-size: 0.6875rem;
        font-weight: 700;
        letter-spacing: 0.1em;
        text-transform: uppercase;
        color: rgb(100 116 139);
      }
      .subs-timeline-month:first-child {
        margin-top: 0;
      }
      .subs-timeline-day {
        position: relative;
        padding-bottom: 1.5rem;
      }
      .subs-timeline-day:last-child {
        padding-bottom: 0.25rem;
      }
      .subs-timeline-date-chip {
        position: absolute;
        left: -4.75rem;
        top: 0;
        z-index: 2;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        width: 3.5rem;
        min-height: 3.5rem;
        padding: 0.35rem 0.25rem;
        border-radius: 0.75rem;
        border: 2px solid rgb(226 232 240);
        background: rgb(255 255 255);
        box-shadow: 0 1px 3px rgb(15 23 42 / 0.08);
      }
      .subs-timeline-day-num {
        font-size: 1.125rem;
        font-weight: 800;
        line-height: 1;
        color: rgb(15 23 42);
        font-variant-numeric: tabular-nums;
      }
      .subs-timeline-month-lbl {
        margin-top: 0.15rem;
        font-size: 0.625rem;
        font-weight: 700;
        letter-spacing: 0.06em;
        color: rgb(100 116 139);
      }
      .subs-timeline-day[data-urgency='urgent'] .subs-timeline-date-chip {
        border-color: rgb(251 191 36);
        background: rgb(255 251 235);
      }
      .subs-timeline-day[data-urgency='urgent'] .subs-timeline-day-num {
        color: rgb(180 83 9);
      }
      .subs-timeline-day[data-urgency='soon'] .subs-timeline-date-chip {
        border-color: rgb(94 234 212);
        background: rgb(240 253 250);
      }
      .subs-timeline-day[data-urgency='soon'] .subs-timeline-day-num {
        color: rgb(15 118 110);
      }
      .subs-timeline-month-chip .subs-timeline-day-num {
        font-size: 0.75rem;
        letter-spacing: 0.04em;
      }
      .subs-timeline-group-more {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.25rem;
        padding: 0.5rem 0 0.25rem;
      }
      .subs-timeline-day-head {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: space-between;
        gap: 0.5rem 1rem;
        margin-bottom: 0.625rem;
      }
      .subs-timeline-day-head.is-collapsed {
        margin-bottom: 0;
      }
      .subs-timeline-day-toggle {
        width: 100%;
        padding: 0.55rem 0.75rem;
        border: 1px solid rgb(226 232 240);
        border-radius: 0.75rem;
        background: rgb(255 255 255);
        cursor: pointer;
        text-align: left;
        transition:
          border-color 0.15s ease,
          background-color 0.15s ease,
          box-shadow 0.15s ease;
      }
      .subs-timeline-day-toggle:hover {
        border-color: rgb(153 246 228);
        background: rgb(240 253 250);
        box-shadow: 0 1px 3px rgb(15 23 42 / 0.05);
      }
      .subs-timeline-day-toggle-chevron {
        flex-shrink: 0;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 1.5rem;
        height: 1.5rem;
        border-radius: 9999px;
        background: rgb(241 245 249);
        color: rgb(100 116 139);
        font-size: 0.625rem;
        transition: transform 0.15s ease;
      }
      .subs-timeline-day-toggle[aria-expanded='true'] .subs-timeline-day-toggle-chevron {
        background: rgb(204 251 241);
        color: rgb(15 118 110);
      }
      .subs-timeline-card {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        padding: 0.65rem 0.85rem;
        border: 1px solid rgb(226 232 240);
        border-radius: 0.75rem;
        background: rgb(255 255 255);
        cursor: pointer;
        transition:
          border-color 0.15s ease,
          background-color 0.15s ease,
          box-shadow 0.15s ease,
          transform 0.15s ease;
      }
      .subs-timeline-card:hover {
        border-color: rgb(153 246 228);
        background: rgb(240 253 250);
        box-shadow: 0 4px 12px rgb(15 23 42 / 0.06);
        transform: translateY(-1px);
      }
      .subs-timeline-card[data-urgency='urgent'] {
        border-left: 3px solid rgb(245 158 11);
      }
      .subs-timeline-card[data-urgency='soon'] {
        border-left: 3px solid rgb(20 184 166);
      }
      .subs-timeline-card[data-urgency='later'] {
        border-left: 3px solid rgb(203 213 225);
      }
      .subs-timeline-countdown {
        display: inline-flex;
        align-items: center;
        gap: 0.25rem;
        border-radius: 9999px;
        padding: 0.15rem 0.5rem;
        font-size: 0.625rem;
        font-weight: 600;
        letter-spacing: 0.02em;
      }
      .subs-timeline-countdown[data-urgency='urgent'] {
        background: rgb(254 243 199);
        color: rgb(180 83 9);
      }
      .subs-timeline-countdown[data-urgency='soon'] {
        background: rgb(204 251 241);
        color: rgb(15 118 110);
      }
      .subs-timeline-countdown[data-urgency='later'] {
        background: rgb(241 245 249);
        color: rgb(100 116 139);
      }
      :host ::ng-deep .subs-notes-tooltip .p-tooltip-text {
        max-width: 22rem;
        white-space: pre-wrap;
        line-height: 1.4;
      }
      .subs-timeline-scroll {
        background: linear-gradient(
          165deg,
          rgb(226 232 240) 0%,
          rgb(218 226 236) 42%,
          rgb(226 232 240) 100%
        );
      }
    `
  ],
  imports: [
    CommonModule,
    PERMISSION_UI_DIRECTIVES,
    RouterLink,
    ReactiveFormsModule,
    FormsModule,
    TableModule,
    ButtonModule,
    SkeletonModule,
    FloatLabelModule,
    DatePickerModule,
    SelectModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    TagModule,
    TooltipModule,
    DrawerModule,
    SubscriptionReadComponent,
    SaleReadComponent,
    CustomerReadComponent
  ]
})
export class SubscriptionsListComponent {
  private subscriptionService = inject(SubscriptionService);
  private authService = inject(AuthService);
  private timelineService = inject(SubscriptionTimelineService);
  private saleService = inject(SaleService);
  private customerService = inject(CustomerService);
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private toastr = inject(ToastrService);

  readonly canExportSubscriptions = this.authService.hasRole('SUBSCRIPTION_EXPORT');
  exporting = signal(false);

  get exportButtonTooltip(): string {
    if (!this.canExportSubscriptions) {
      return 'No tienes permiso para descargar este listado. Pide acceso a un administrador.';
    }
    if (this.totalItems() === 0) {
      return 'No hay suscripciones para exportar con los filtros actuales';
    }
    return 'Descargar todas las suscripciones filtradas en CSV';
  }

  selectedSubscription = signal<SubscriptionDTO | null>(null);
  isDetailsOpen = signal(false);
  detailsLoading = signal(false);

  isSaleDetailsOpen = signal(false);
  selectedSale = signal<SaleDTO | null>(null);
  saleCharges = signal<ChargeDTO[]>([]);
  saleDetailsLoading = signal(false);

  isCustomerDetailsOpen = signal(false);
  selectedCustomer = signal<CustomerDTO | null>(null);
  customerDetailsLoading = signal(false);

  deleteModalOpen = signal(false);
  deleteModalShown = signal(false);
  subscriptionToDelete = signal<SubscriptionDTO | null>(null);
  deleteLoading = signal(false);

  viewMode = signal<'table' | 'timeline'>('table');
  timelineGrouping = signal<TimelineGroupMode>('day');
  /** Debe coincidir con TIMELINE_HORIZON_MONTHS del API (24 meses). */
  readonly timelineHorizonYears = 2;

  subscriptions = signal<SubscriptionDTO[]>([]);
  loading = signal(false);
  initialLoadDone = signal(false);
  error = signal<string | null>(null);
  currentPage = signal(0);
  pageSize = signal(20);
  totalItems = signal(0);
  totalPages = signal(0);
  pageSizeOptions = [20, 50, 100];
  sortField = signal('createdAt');
  sortOrder = signal(-1);

  timelineLoading = computed(() => this.timelineService.loading());
  timelineLoadingMore = computed(() => this.timelineService.loadingMore());
  timelineHasMore = computed(() => this.timelineService.hasMore());
  timelineGroups = computed(() => {
    this.timelineService.expandedGroups();
    this.timelineService.groupDetails();
    this.timelineService.groupDetailMeta();
    return this.timelineService.collapsedGroups().map((group) =>
      this.toTimelineGroup(
        group.date,
        group.renewalCount,
        group.totalAmount,
        this.timelineService.getGroupItems(group.date),
        this.timelineGrouping()
      )
    );
  });

  skeletonRows = [1, 2, 3, 4, 5, 6, 7, 8];

  timelineScrollHost = viewChild<ElementRef<HTMLElement>>('timelineScrollHost');
  timelineScrollSentinel = viewChild<ElementRef<HTMLElement>>('timelineScrollSentinel');
  private timelineScrollObserver?: IntersectionObserver;
  private groupScrollObservers = new Map<string, IntersectionObserver>();

  private readonly allFilterValue = 'ALL';

  filtersForm = this.fb.group({
    number: [''],
    q: [''],
    status: [this.allFilterValue],
    state: [EntityState.ACTIVE],
    autoInvoice: [this.allFilterValue],
    createdAtRange: this.fb.control<Date[] | null>(null)
  });

  hasActiveFilters = signal(false);

  statusOptions: { value: SubscriptionStatusEnumValue; label: string }[] = [
    { value: 'PENDING_PAYMENT', label: 'Pendiente de pago' },
    { value: 'ACTIVE', label: 'Activa' },
    { value: 'PAUSED', label: 'Pausada' },
    { value: 'CANCELED', label: 'Cancelada' },
    { value: 'EXPIRED', label: 'Expirada' }
  ];

  statusFilterOptions = [
    { value: this.allFilterValue, label: 'Todos' },
    ...this.statusOptions
  ];

  stateFilterOptions = [
    { value: this.allFilterValue, label: 'Todos' },
    { value: EntityState.ACTIVE, label: 'Activo' },
    { value: EntityState.INACTIVE, label: 'Inactivo' }
  ];

  autoInvoiceFilterOptions = [
    { value: this.allFilterValue, label: 'Todas' },
    { value: 'true', label: 'Sí' },
    { value: 'false', label: 'No' }
  ];

  frequencyOptions: { value: FrequencyEnumValue; label: string }[] = [
    { value: 'ONCE', label: 'Una vez' },
    { value: 'DAILY', label: 'Diario' },
    { value: 'WEEKLY', label: 'Semanal' },
    { value: 'BIWEEKLY', label: 'Quincenal' },
    { value: 'MONTHLY', label: 'Mensual' },
    { value: 'QUARTERLY', label: 'Trimestral' },
    { value: 'SEMIANNUALLY', label: 'Semestral' },
    { value: 'ANNUALLY', label: 'Anual' }
  ];

  constructor() {
    this.filtersForm.controls.number.valueChanges.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      takeUntilDestroyed()
    ).subscribe(() => this.applyFilters());

    this.filtersForm.controls.q.valueChanges.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      takeUntilDestroyed()
    ).subscribe(() => this.applyFilters());

    merge(
      this.filtersForm.controls.status.valueChanges,
      this.filtersForm.controls.state.valueChanges,
      this.filtersForm.controls.autoInvoice.valueChanges,
      this.filtersForm.controls.createdAtRange.valueChanges
    ).pipe(takeUntilDestroyed()).subscribe(() => this.applyFilters());

    effect(() => {
      if (this.viewMode() !== 'timeline') {
        this.disconnectTimelineScrollObserver();
        this.disconnectGroupScrollObservers();
        return;
      }
      this.timelineGrouping();
      this.timelineService.collapsedGroups().length;
      this.timelineService.expandedGroups();
      this.timelineService.groupDetails();
      this.timelineService.groupDetailMeta();
      this.timelineHasMore();
      this.timelineLoading();
      this.timelineLoadingMore();
      queueMicrotask(() => {
        this.bindTimelineScrollObserver();
        this.bindGroupScrollObservers();
      });
    });

    this.loadSubscriptions();
  }

  loadSubscriptions(): void {
    this.loading.set(true);
    this.error.set(null);

    const spec = this.buildSpecFromFilters();
    const direction = this.sortOrder() === 1 ? 'asc' : 'desc';
    const pageable = {
      page: this.currentPage(),
      size: this.pageSize(),
      sort: [`${this.sortField()},${direction}`]
    };

    this.subscriptionService.listSubscriptions(spec, pageable).subscribe({
      next: (response) => {
        if (response.ok) {
          this.subscriptions.set(response.body ?? []);
          if (response.page) {
            this.totalItems.set(response.page.totalItems ?? 0);
            this.totalPages.set(response.page.totalPages ?? 0);
            this.pageSize.set(response.page.pageSize ?? 20);
            this.currentPage.set(Math.max(0, (response.page.currentPage ?? 1) - 1));
          } else {
            this.totalItems.set(response.body?.length ?? 0);
          }
        } else {
          this.error.set(response.message || 'Error al cargar suscripciones');
        }
        this.loading.set(false);
        this.initialLoadDone.set(true);
      },
      error: (err) => {
        this.error.set('Error al conectar con el servidor.');
        this.loading.set(false);
        this.initialLoadDone.set(true);
      }
    });
  }

  onLazyLoad(event: TableLazyLoadEvent): void {
    if (this.viewMode() === 'timeline') return;
    const size = event.rows ?? this.pageSize();
    const rawSortField = Array.isArray(event.sortField) ? event.sortField[0] : event.sortField;
    const nextSortField = rawSortField || this.sortField();
    const nextSortOrder = event.sortOrder ?? this.sortOrder();
    const sortChanged =
      nextSortField !== this.sortField() || nextSortOrder !== this.sortOrder();
    const page = sortChanged
      ? 0
      : size
        ? Math.floor((event.first ?? 0) / size)
        : 0;

    if (!sortChanged && page === this.currentPage() && size === this.pageSize()) return;

    this.sortField.set(nextSortField);
    this.sortOrder.set(nextSortOrder);
    this.currentPage.set(page);
    this.pageSize.set(size);
    this.loadSubscriptions();
  }

  /** Recarga el listado sin resetear filtros ni página. */
  refreshTable(): void {
    if (this.viewMode() === 'timeline') {
      this.loadTimelineRenewals(false);
    } else {
      this.loadSubscriptions();
    }
  }

  exportCsv(): void {
    if (!this.canExportSubscriptions || this.exporting()) return;
    this.exporting.set(true);
    this.subscriptionService.exportSubscriptionsCsv(this.buildSpecFromFilters(), ['createdAt,desc']).subscribe({
      next: ({ blob, filename }) => {
        this.triggerBrowserDownload(blob, filename);
        this.exporting.set(false);
        this.toastr.success('CSV de suscripciones descargado');
      },
      error: (err: HttpErrorResponse) => {
        this.exporting.set(false);
        const fallback = 'No se pudo exportar el CSV de suscripciones';
        if (err.error instanceof Blob) {
          err.error.text().then((text) => {
            try {
              const json = JSON.parse(text) as { message?: string };
              this.toastr.error(json?.message?.trim() || fallback);
            } catch {
              this.toastr.error(fallback);
            }
          });
          return;
        }
        this.toastr.error(httpErrorApiMessage(err, fallback));
        console.error('Error exporting subscriptions CSV:', err);
      }
    });
  }

  private triggerBrowserDownload(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'suscripciones.csv';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  setTimelineGrouping(mode: TimelineGroupMode): void {
    if (this.timelineGrouping() === mode) return;
    this.timelineGrouping.set(mode);
    this.timelineService.setGroupMode(mode);
    this.loadTimelineRenewals(false);
  }

  setViewMode(mode: 'table' | 'timeline'): void {
    if (this.viewMode() === mode) return;
    this.viewMode.set(mode);
    if (mode === 'timeline') {
      this.timelineService.setGroupMode(this.timelineGrouping());
      this.loadTimelineRenewals(false);
    } else {
      this.loadSubscriptions();
    }
  }

  applyFilters(): void {
    this.syncActiveFilters();
    if (this.viewMode() !== 'table') return;
    this.currentPage.set(0);
    this.loadSubscriptions();
  }

  clearFilters(): void {
    this.filtersForm.reset(
      {
        number: '',
        q: '',
        status: this.allFilterValue,
        state: EntityState.ACTIVE,
        autoInvoice: this.allFilterValue,
        createdAtRange: null
      },
      { emitEvent: false }
    );
    this.applyFilters();
  }

  private syncActiveFilters(): void {
    const raw = this.filtersForm.getRawValue();
    this.hasActiveFilters.set(
      !!(
        raw.number?.trim() ||
        raw.q?.trim() ||
        this.isSpecificFilter(raw.status) ||
        raw.state !== EntityState.ACTIVE ||
        this.isSpecificFilter(raw.autoInvoice) ||
        this.hasCompleteDateRange(raw.createdAtRange)
      )
    );
  }

  private isSpecificFilter(value: string | null | undefined): boolean {
    return !!value && value !== this.allFilterValue;
  }

  private hasCompleteDateRange(range: Date[] | null | undefined): boolean {
    return !!range?.[0] && !!range?.[1];
  }

  openSubscriptionDetails(sub: SubscriptionDTO): void {
    if (!sub?.id) return;
    this.isDetailsOpen.set(true);
    this.selectedSubscription.set(null);
    this.detailsLoading.set(true);
    this.subscriptionService.getSubscriptionById(sub.id).subscribe({
      next: (response) => {
        if (response.ok && response.body) {
          this.selectedSubscription.set(response.body);
        } else {
          this.toastr.error(response.message || 'No se pudo cargar el detalle');
          this.closeDetails();
        }
        this.detailsLoading.set(false);
      },
      error: () => {
        this.detailsLoading.set(false);
        this.toastr.error('Error al cargar el detalle de la suscripción');
        this.closeDetails();
      }
    });
  }

  closeDetails(): void {
    this.isDetailsOpen.set(false);
    this.selectedSubscription.set(null);
    this.detailsLoading.set(false);
  }

  onDrawerVisibleChange(visible: boolean): void {
    if (!visible) this.closeDetails();
  }

  editFromModal(): void {
    const sub = this.selectedSubscription();
    if (!sub?.id) return;
    this.closeDetails();
    this.router.navigate(['/subscriptions', sub.id]);
  }

  openSaleDetails(saleId: number): void {
    if (!saleId) return;
    this.isSaleDetailsOpen.set(true);
    this.selectedSale.set(null);
    this.saleCharges.set([]);
    this.saleDetailsLoading.set(true);
    this.saleService.getSaleById(saleId).subscribe({
      next: (response) => {
        if (response.ok && response.body) {
          const detail = response.body;
          this.selectedSale.set(detail);
          this.saleService.getSaleCharges(detail.id!).subscribe({
            next: (chargesResp) => {
              this.saleCharges.set(chargesResp.body ?? []);
            },
            error: () => this.saleCharges.set([])
          });
        } else {
          this.toastr.error(response.message || 'No se pudo cargar el detalle');
          this.closeSaleDetails();
        }
        this.saleDetailsLoading.set(false);
      },
      error: () => {
        this.saleDetailsLoading.set(false);
        this.toastr.error('Error al cargar el detalle de la venta');
        this.closeSaleDetails();
      }
    });
  }

  closeSaleDetails(): void {
    this.isSaleDetailsOpen.set(false);
    this.selectedSale.set(null);
    this.saleCharges.set([]);
    this.saleDetailsLoading.set(false);
  }

  onSaleDrawerVisibleChange(visible: boolean): void {
    if (!visible) this.closeSaleDetails();
  }

  editSaleFromModal(): void {
    const sale = this.selectedSale();
    if (!sale?.id) return;
    this.closeSaleDetails();
    this.router.navigate(['/sales', sale.id]);
  }

  openCustomerDetails(customerId: number): void {
    if (!customerId) return;
    this.isCustomerDetailsOpen.set(true);
    this.selectedCustomer.set(null);
    this.customerDetailsLoading.set(true);
    this.customerService.getCustomerById(customerId).subscribe({
      next: (response) => {
        if (response.ok && response.body) {
          this.selectedCustomer.set(response.body);
        } else {
          this.toastr.error(response.message || 'No se pudo cargar el cliente');
          this.closeCustomerDetails();
        }
        this.customerDetailsLoading.set(false);
      },
      error: () => {
        this.customerDetailsLoading.set(false);
        this.toastr.error('Error al cargar el cliente');
        this.closeCustomerDetails();
      }
    });
  }

  closeCustomerDetails(): void {
    this.isCustomerDetailsOpen.set(false);
    this.selectedCustomer.set(null);
    this.customerDetailsLoading.set(false);
  }

  onCustomerDrawerVisibleChange(visible: boolean): void {
    if (!visible) this.closeCustomerDetails();
  }

  editCustomerFromModal(): void {
    const customer = this.selectedCustomer();
    if (!customer?.id) return;
    this.closeCustomerDetails();
    this.router.navigate(['/customers', customer.id]);
  }

  openSubscriptionDetailsById(subscriptionId: number): void {
    if (!subscriptionId) return;
    this.closeSaleDetails();
    const current = this.selectedSubscription();
    if (current?.id === subscriptionId) {
      this.isDetailsOpen.set(true);
      return;
    }
    this.isDetailsOpen.set(true);
    this.selectedSubscription.set(null);
    this.detailsLoading.set(true);
    this.subscriptionService.getSubscriptionById(subscriptionId).subscribe({
      next: (response) => {
        if (response.ok && response.body) {
          this.selectedSubscription.set(response.body);
        } else {
          this.toastr.error(response.message || 'No se pudo cargar la suscripción');
          this.closeDetails();
        }
        this.detailsLoading.set(false);
      },
      error: () => {
        this.detailsLoading.set(false);
        this.toastr.error('Error al cargar la suscripción');
        this.closeDetails();
      }
    });
  }

  editSubscription(sub: SubscriptionDTO, event?: MouseEvent): void {
    event?.stopPropagation();
    if (!sub?.id) return;
    this.router.navigate(['/subscriptions', sub.id]);
  }

  openPublicLink(sub: SubscriptionDTO, event?: MouseEvent): void {
    event?.stopPropagation();
    if (!sub?.id) return;
    this.subscriptionService.createSubscriptionPublicHash(sub.id).subscribe({
      next: (r) => {
        if (!r.ok || !r.body) {
          this.toastr.error(r.message || 'No se pudo generar el enlace de pago');
          return;
        }
        const url = `${window.location.origin}/public/subscriptions/${sub.id}?hash=${encodeURIComponent(r.body)}`;
        window.open(url, '_blank');
      },
      error: (err) => this.toastr.error(err.error?.message ?? 'Error al generar el enlace de pago')
    });
  }

  openDeleteModal(sub: SubscriptionDTO, event?: MouseEvent): void {
    event?.stopPropagation();
    if (!sub?.id) return;
    this.subscriptionToDelete.set(sub);
    this.deleteModalOpen.set(true);
    setTimeout(() => this.deleteModalShown.set(true), 10);
  }

  closeDeleteModal(): void {
    this.deleteModalShown.set(false);
    setTimeout(() => {
      this.deleteModalOpen.set(false);
      this.subscriptionToDelete.set(null);
    }, 200);
  }

  confirmDeleteSubscription(): void {
    const sub = this.subscriptionToDelete();
    if (!sub?.id) return;
    this.deleteLoading.set(true);
    this.subscriptionService.deleteSubscription(sub.id)
      .pipe(finalize(() => this.deleteLoading.set(false)))
      .subscribe({
        next: (response) => {
          if (response.ok) {
            this.toastr.success('Suscripción eliminada');
            if (this.viewMode() === 'timeline') {
              this.loadTimelineRenewals(false);
            } else {
              this.loadSubscriptions();
            }
            this.closeDeleteModal();
          } else {
            this.toastr.error(response.message || 'Error al eliminar');
          }
        },
        error: (err) => {
          this.toastr.error(err.error?.message || 'Error al eliminar');
        }
      });
  }

  getStatusLabel(status: SubscriptionStatusEnumValue | string | undefined): string {
    if (!status) return '—';
    if (status === 'CANCELED' || status === 'CANCELLED') return 'Cancelada';
    return this.statusOptions.find((o) => o.value === status)?.label ?? String(status);
  }

  getStatusSeverity(status: SubscriptionStatusEnumValue | string | undefined): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    switch (status) {
      case 'ACTIVE': return 'success';
      case 'PENDING_PAYMENT': return 'warn';
      case 'PAUSED': return 'info';
      case 'CANCELED':
      case 'CANCELLED': return 'danger';
      case 'EXPIRED': return 'secondary';
      default: return 'secondary';
    }
  }

  getStatusIcon(sub: SubscriptionDTO | null | undefined): string {
    if (this.isSubscriptionInactive(sub)) return 'pi pi-trash';
    const status = sub?.status;
    switch (status) {
      case 'PENDING_PAYMENT': return 'pi pi-clock';
      case 'ACTIVE': return 'pi pi-check-circle';
      case 'PAUSED': return 'pi pi-pause';
      case 'CANCELED':
      case 'CANCELLED': return 'pi pi-ban';
      case 'EXPIRED': return 'pi pi-calendar-times';
      default: return 'pi pi-sync';
    }
  }

  isSubscriptionInactive(sub: SubscriptionDTO | null | undefined): boolean {
    return sub?.state === EntityState.INACTIVE;
  }

  getStatusIconClasses(sub: SubscriptionDTO | null | undefined): string {
    const base = 'shrink-0 h-10 w-10 rounded-full flex items-center justify-center';
    if (this.isSubscriptionInactive(sub)) return `${base} bg-red-100 text-red-600`;
    const status = sub?.status;
    switch (status) {
      case 'PENDING_PAYMENT': return `${base} bg-amber-100 text-amber-600`;
      case 'ACTIVE': return `${base} bg-emerald-100 text-emerald-600`;
      case 'PAUSED': return `${base} bg-sky-100 text-sky-600`;
      case 'CANCELED':
      case 'CANCELLED': return `${base} bg-rose-100 text-rose-600`;
      case 'EXPIRED': return `${base} bg-slate-200 text-slate-600`;
      default: return `${base} bg-slate-100 text-slate-600`;
    }
  }

  getFrequencyLabel(freq: FrequencyEnumValue | string | undefined): string {
    if (!freq) return '—';
    return this.frequencyOptions.find((o) => o.value === freq)?.label ?? String(freq);
  }

  private readonly collectionMethodLabels: Record<CollectionMethodEnumValue, string> = {
    [CollectionMethodEnum.LINK]: 'Liga de pago',
    [CollectionMethodEnum.RECURRING_CHARGE]: 'Cobro recurrente',
    [CollectionMethodEnum.MANUAL]: 'Manual',
    [CollectionMethodEnum.MERCHANT_INITIATED]: 'Cobro bajo demanda'
  };

  getCollectionMethodLabel(method: CollectionMethodEnumValue | string | undefined): string {
    if (!method) return '—';
    return this.collectionMethodLabels[method as CollectionMethodEnumValue] ?? String(method);
  }

  loadTimelineRenewals(append: boolean): void {
    if (append) {
      if (this.timelineLoadingMore() || this.timelineLoading() || !this.timelineHasMore()) return;
    } else {
      this.error.set(null);
    }

    this.timelineService.loadCollapsedGroups(append).subscribe({
      next: (response) => {
        if (!response.ok && !append) {
          this.error.set(response.message || 'Error al cargar renovaciones');
        }
        this.initialLoadDone.set(true);
        queueMicrotask(() => this.bindTimelineScrollObserver());
      },
      error: () => {
        this.error.set('Error al conectar con el servidor.');
        this.initialLoadDone.set(true);
      }
    });
  }

  onTimelineScroll(event: Event): void {
    if (!this.timelineHasMore() || this.timelineLoadingMore() || this.timelineLoading()) return;
    const el = event.target as HTMLElement;
    const threshold = 160;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - threshold) {
      this.loadTimelineRenewals(true);
    }
  }

  private bindTimelineScrollObserver(): void {
    this.disconnectTimelineScrollObserver();
    if (this.viewMode() !== 'timeline' || !this.timelineHasMore()) return;

    const host = this.timelineScrollHost()?.nativeElement;
    const sentinel = this.timelineScrollSentinel()?.nativeElement;
    if (!host || !sentinel) return;

    this.timelineScrollObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.loadTimelineRenewals(true);
        }
      },
      { root: host, rootMargin: '0px 0px 160px 0px', threshold: 0 }
    );
    this.timelineScrollObserver.observe(sentinel);
  }

  private bindGroupScrollObservers(): void {
    const host = this.timelineScrollHost()?.nativeElement;
    if (!host) return;

    const activeKeys = new Set<string>();
    for (const group of this.timelineGroups()) {
      if (!this.isTimelineGroupExpanded(group.dateKey)) continue;
      if (!this.hasMoreTimelineGroupItems(group.dateKey)) continue;

      const sentinel = host.querySelector<HTMLElement>(
        `[data-group-sentinel="${group.dateKey}"]`
      );
      if (!sentinel) continue;

      activeKeys.add(group.dateKey);
      if (this.groupScrollObservers.has(group.dateKey)) continue;

      const observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            this.loadMoreTimelineGroupItems(group.dateKey);
          }
        },
        { root: host, rootMargin: '0px 0px 80px 0px', threshold: 0 }
      );
      observer.observe(sentinel);
      this.groupScrollObservers.set(group.dateKey, observer);
    }

    for (const [key, observer] of this.groupScrollObservers) {
      if (!activeKeys.has(key)) {
        observer.disconnect();
        this.groupScrollObservers.delete(key);
      }
    }
  }

  private disconnectGroupScrollObservers(): void {
    for (const observer of this.groupScrollObservers.values()) {
      observer.disconnect();
    }
    this.groupScrollObservers.clear();
  }

  private disconnectTimelineScrollObserver(): void {
    this.timelineScrollObserver?.disconnect();
    this.timelineScrollObserver = undefined;
  }

  getDaysUntilRenewal(sub: SubscriptionDTO): number | null {
    return calendarDaysUntil(sub.nextBillingDate);
  }

  formatRenewalDate(value: string | undefined | null): string {
    return formatApiDateOnly(value) || '—';
  }

  getRenewalRelativeLabel(sub: SubscriptionDTO): string {
    const days = this.getDaysUntilRenewal(sub);
    if (days == null) return '—';
    if (days === 0) return 'Hoy';
    if (days === 1) return 'Mañana';
    if (days > 1) return `En ${days} días`;
    if (days === -1) return 'Ayer';
    return `Hace ${Math.abs(days)} días`;
  }

  getRenewalUrgency(sub: SubscriptionDTO): RenewalUrgency {
    const days = this.getDaysUntilRenewal(sub);
    if (days == null) return 'later';
    if (days <= 7) return 'urgent';
    if (days <= 30) return 'soon';
    return 'later';
  }

  formatTimelineMoney(amount: number, currency: string | null): string {
    if (currency == null) {
      return amount.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    return amount.toLocaleString('es-MX', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  formatSubscriptionMoney(sub: SubscriptionDTO): string {
    return this.formatTimelineMoney(sub.total ?? 0, sub.currency || 'MXN');
  }

  /** El API envía el cliente anidado (`customer`), ya no `customerName` plano. */
  getCustomerName(sub: SubscriptionDTO | null | undefined): string {
    const customer = sub?.customer;
    return (
      customer?.comercialName?.trim()
      || customer?.legalName?.trim()
      || sub?.customerName?.trim()
      || ''
    );
  }

  getCustomerEmail(sub: SubscriptionDTO | null | undefined): string {
    return sub?.customer?.email?.trim() || sub?.customerEmail?.trim() || '';
  }

  getSubscriptionNotesTooltip(sub: SubscriptionDTO): string {
    return sub.notes?.trim() ?? '';
  }

  hasSubscriptionNotes(sub: SubscriptionDTO): boolean {
    return !!sub.notes?.trim();
  }

  isTimelineGroupExpanded(groupKey: string): boolean {
    return this.timelineService.isGroupExpanded(groupKey);
  }

  isTimelineGroupLoading(groupKey: string): boolean {
    return this.timelineService.isGroupLoading(groupKey);
  }

  isTimelineGroupLoadingMore(groupKey: string): boolean {
    return this.timelineService.isGroupLoadingMore(groupKey);
  }

  hasMoreTimelineGroupItems(groupKey: string): boolean {
    return this.timelineService.hasMoreGroupItems(groupKey);
  }

  toggleTimelineGroup(groupKey: string, event?: Event): void {
    event?.stopPropagation();
    this.timelineService.toggleGroup(groupKey);
  }

  loadMoreTimelineGroupItems(groupKey: string): void {
    this.timelineService.loadMoreGroupItems(groupKey);
  }

  isNewTimelineMonth(group: TimelineRenewalGroup, index: number): boolean {
    if (this.timelineGrouping() === 'month') return false;
    if (index === 0) return true;
    return this.timelineGroups()[index - 1]?.monthKey !== group.monthKey;
  }

  private sumSubscriptionTotals(items: SubscriptionDTO[]): { amount: number; currency: string | null } {
    if (!items.length) return { amount: 0, currency: 'MXN' };
    const currencies = new Set(items.map((s) => s.currency || 'MXN'));
    const amount = items.reduce((acc, s) => acc + (s.total ?? 0), 0);
    if (currencies.size > 1) return { amount, currency: null };
    return { amount, currency: items[0].currency || 'MXN' };
  }

  private toTimelineGroup(
    dateKey: string,
    count: number,
    totalAmount: number,
    items: SubscriptionDTO[],
    grouping: TimelineGroupMode
  ): TimelineRenewalGroup {
    const totals = items.length
      ? this.sumSubscriptionTotals(items)
      : { amount: totalAmount, currency: null as string | null };

    if (dateKey === 'sin-fecha') {
      return {
        dateKey,
        title: 'Sin fecha de cobro',
        subtitle: '',
        dayNumber: '—',
        monthLabel: 'N/D',
        monthKey: 'sin-fecha',
        monthTitle: 'Sin programar',
        count,
        totalAmount: totals.amount,
        totalCurrency: totals.currency,
        items,
        urgency: 'later'
      };
    }

    if (grouping === 'month') {
      const monthStart = parseApiCalendarDate(`${dateKey}-01`);
      if (!monthStart) {
        return {
          dateKey,
          title: dateKey,
          subtitle: '',
          dayNumber: '—',
          monthLabel: 'N/D',
          monthKey: dateKey,
          monthTitle: dateKey,
          count,
          totalAmount: totals.amount,
          totalCurrency: totals.currency,
          items,
          urgency: 'later'
        };
      }

      const localized = monthStart.setLocale('es-MX');
      const monthTitle = localized.toFormat('MMMM yyyy');
      const monthLabel = localized.toFormat('yyyy');
      const monthShort = localized.toFormat('MMM').replace('.', '').toUpperCase().slice(0, 3);
      const daysUntil = calendarDaysUntil(`${dateKey}-01`);

      let subtitle = '';
      if (daysUntil != null && daysUntil > 0) {
        subtitle = `Próximo cobro en ${daysUntil} días`;
      } else if (daysUntil === 0) {
        subtitle = 'Cobros este mes';
      }

      return {
        dateKey,
        title: monthTitle.charAt(0).toUpperCase() + monthTitle.slice(1),
        subtitle,
        dayNumber: monthShort,
        monthLabel,
        monthKey: dateKey,
        monthTitle: monthTitle.charAt(0).toUpperCase() + monthTitle.slice(1),
        count,
        totalAmount: totals.amount,
        totalCurrency: totals.currency,
        items,
        urgency: daysUntil != null && daysUntil <= 30 ? (daysUntil <= 7 ? 'urgent' : 'soon') : 'later'
      };
    }

    const date = parseApiCalendarDate(dateKey);
    if (!date) {
      return {
        dateKey,
        title: dateKey,
        subtitle: '',
        dayNumber: '—',
        monthLabel: 'N/D',
        monthKey: dateKey.slice(0, 7),
        monthTitle: dateKey,
        count,
        totalAmount: totals.amount,
        totalCurrency: totals.currency,
        items,
        urgency: 'later'
      };
    }
    const days = this.getDaysUntilRenewal({ nextBillingDate: dateKey } as SubscriptionDTO);
    const localized = date.setLocale('es-MX');
    let title: string;
    if (days === 0) title = 'Hoy';
    else if (days === 1) title = 'Mañana';
    else {
      title = localized.toFormat("cccc d 'de' MMMM");
      title = title.charAt(0).toUpperCase() + title.slice(1);
    }

    let subtitle = '';
    if (days === 0 || days === 1) {
      subtitle = localized.toFormat('cccc d MMM');
      subtitle = subtitle.charAt(0).toUpperCase() + subtitle.slice(1);
    } else if (days != null && days > 1) {
      subtitle = `En ${days} días`;
    }

    const monthTitle = localized.toFormat('MMMM yyyy');
    const monthLabel = localized.toFormat('MMM').replace('.', '').toUpperCase().slice(0, 3);

    return {
      dateKey,
      title,
      subtitle,
      dayNumber: String(date.day),
      monthLabel,
      monthKey: dateKey.slice(0, 7),
      monthTitle: monthTitle.charAt(0).toUpperCase() + monthTitle.slice(1),
      count,
      totalAmount: totals.amount,
      totalCurrency: totals.currency,
      items,
      urgency: this.getRenewalUrgency({ nextBillingDate: dateKey } as SubscriptionDTO)
    };
  }

  private buildSpecFromFilters(): ListSubscriptionFilterSpec {
    const value = this.filtersForm.getRawValue();
    const spec: ListSubscriptionFilterSpec = {};
    const number = value.number?.trim();
    const q = value.q?.trim();
    if (number) {
      spec['number[lk]'] = number;
    }
    if (q) {
      spec['q[lk]'] = q;
    }
    if (this.isSpecificFilter(value.status)) {
      spec.status = value.status;
    }
    if (this.isSpecificFilter(value.state)) {
      spec.state = value.state;
    } else {
      spec.state = [EntityState.ACTIVE, EntityState.INACTIVE];
    }
    if (this.isSpecificFilter(value.autoInvoice)) {
      spec.autoInvoice = value.autoInvoice;
    }
    this.applyCreatedAtRange(spec, value.createdAtRange);
    return spec;
  }

  private applyCreatedAtRange(spec: ListSubscriptionFilterSpec, range: Date[] | null | undefined): void {
    if (!this.hasCompleteDateRange(range) || !range?.[0] || !range[1]) return;
    const from = new Date(range[0]);
    from.setHours(0, 0, 0, 0);
    const toExclusive = new Date(range[1]);
    toExclusive.setHours(0, 0, 0, 0);
    toExclusive.setDate(toExclusive.getDate() + 1);
    spec['createdAt[gte]'] = from.toISOString();
    spec['createdAt[lt]'] = toExclusive.toISOString();
  }
}
